// Implements: F-UIX-018, F-USG-008
/**
 * One row of the distribution (UIX.6): a person's limit or a team's allocation. Presentation
 * with the row's own typing state; the write itself is the caller's (`onSave`, `onClear`).
 *
 * - What is typed is checked before it is sent (`personLimitProblem`, `teamAllocationProblem`);
 *   a problem is shown in the row and nothing is sent.
 * - A write the server refuses shows its sentence in the row and **keeps what was typed**, so the
 *   person corrects it rather than typing it again. A failure the server did not word for people
 *   reads "No se pudo guardar"; nothing is ever retried for them (checklist 13).
 * - A row with no control (`editable: false`) shows the saved values as text: the server's
 *   `can_manage` decides, never a role. A person who left, or a team the list no longer has, is
 *   named for what it is (never by id) and can only be removed.
 * - The value that applies (the stored limit clamped by the ceiling, or the default) is always
 *   shown, in a live cell so a save that changes it is announced, and a limit above the ceiling
 *   says so (N3). An amount being typed is shown as it will be saved, before saving.
 */

import { useId, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Input } from "../../../shared/ui/atoms/Input";
import { moneyText } from "../../../shared/ui/moneyText";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import type {
  CeilingView,
  OrganizationPolicyView,
  PersonLimitInput,
  PersonLimitView,
  WriteOutcome,
} from "../../../types/usagePolicy";
import {
  MAX_MONTHLY_BUDGET_MICROS,
  MAX_MONTHLY_GENERATIONS,
  allocatedMicros,
  parseCount,
  parseUsdMicros,
  personLimitProblem,
  teamAllocationProblem,
  usdInputOf,
} from "../model/distribution";
import styles from "./Distribution.module.css";

const USD = "USD";

type Translate = ReturnType<typeof useTranslation>["t"];

/** The row's message: a problem found before sending, or the server's refusal. */
interface RowMessage {
  readonly text: string;
  /** Which field it is about, so that field is marked invalid; `null` for the row. */
  readonly field: "generations" | "budget" | null;
}

function failureText(outcome: WriteOutcome, t: Translate): string | null {
  return outcome.ok ? null : userMessage(outcome.error, t("backoffice.distribution.problem.failed"));
}

/** The row's pending flag and message, and the one way it sends a write. */
function useRowWrite(): {
  readonly pending: boolean;
  readonly message: RowMessage | null;
  readonly setMessage: (message: RowMessage | null) => void;
  readonly send: (write: () => Promise<WriteOutcome>) => Promise<boolean>;
} {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<RowMessage | null>(null);
  const send = async (write: () => Promise<WriteOutcome>): Promise<boolean> => {
    setPending(true);
    setMessage(null);
    const outcome = await write();
    setPending(false);
    const text = failureText(outcome, t);
    if (text !== null) setMessage({ text, field: null });
    return outcome.ok;
  };
  return { pending, message, setMessage, send };
}

/**
 * The amount a field will be saved as, shown before saving: `1000.5` reads as one thousand dollars
 * and fifty cents, written by the shared money formatter in the browser's locale.
 */
function AmountPreview({ text }: { readonly text: string }): ReactElement | null {
  const { t } = useTranslation();
  const typed = parseUsdMicros(text);
  if (typed.kind !== "value") return null;
  return (
    <span className={styles.secondary} aria-live="polite">
      {t("backoffice.distribution.preview", { amount: moneyText(typed.value, USD) })}
    </span>
  );
}

function RowAlert({ id, message }: { readonly id: string; readonly message: RowMessage | null }): ReactElement | null {
  if (message === null) return null;
  return (
    <p id={id} role="alert" className={styles.problem}>
      {message.text}
    </p>
  );
}

export interface PersonLimitRowProps {
  /** The person's name, or `null` for a saved limit of somebody who left the organization. */
  readonly name: string | null;
  readonly email: string | null;
  readonly saved: PersonLimitView | undefined;
  readonly policy: OrganizationPolicyView;
  /** The server's `can_manage`: without it the row shows values, no control. */
  readonly canManage: boolean;
  readonly onSave: (input: PersonLimitInput) => Promise<WriteOutcome>;
  readonly onClear: () => Promise<WriteOutcome>;
}

function limitProblemText(
  problem: NonNullable<ReturnType<typeof personLimitProblem>>,
  ceiling: CeilingView | null,
  t: Translate,
): RowMessage {
  switch (problem) {
    case "invalidGenerations":
      return { text: t("backoffice.distribution.problem.invalidGenerations"), field: "generations" };
    case "invalidBudget":
      return { text: t("backoffice.distribution.problem.invalidBudget"), field: "budget" };
    case "ambiguousBudget":
      return { text: t("backoffice.distribution.problem.thousands"), field: "budget" };
    case "generationsOutOfBounds":
      return {
        text: t("backoffice.distribution.problem.generationsOutOfBounds", { limit: MAX_MONTHLY_GENERATIONS }),
        field: "generations",
      };
    case "budgetOutOfBounds":
      return {
        text: t("backoffice.distribution.problem.budgetOutOfBounds", { limit: moneyText(MAX_MONTHLY_BUDGET_MICROS, USD) }),
        field: "budget",
      };
    case "generationsAboveCeiling":
      return {
        text: t("backoffice.distribution.problem.generationsAboveCeiling", { limit: ceiling?.max_generations_per_person ?? 0 }),
        field: "generations",
      };
    case "budgetAboveCeiling":
      return {
        text: t("backoffice.distribution.problem.budgetAboveCeiling", {
          limit: moneyText(ceiling?.max_budget_micros_per_person ?? 0, USD),
        }),
        field: "budget",
      };
  }
}

export function PersonLimitRow({ name, email, saved, policy, canManage, onSave, onClear }: PersonLimitRowProps): ReactElement {
  const { t } = useTranslation();
  const generationsId = useId();
  const budgetId = useId();
  const messageId = useId();
  const [generations, setGenerations] = useState(saved?.monthly_generations?.toString() ?? "");
  const [budget, setBudget] = useState(usdInputOf(saved?.monthly_budget_micros ?? null));
  const { pending, message, setMessage, send } = useRowWrite();
  const label = name ?? t("backoffice.distribution.person.formerMember");
  const editable = canManage && name !== null;
  const applies = t("backoffice.distribution.applies", {
    generations: saved?.effective_generations ?? policy.default_generations,
    budget: moneyText(saved?.effective_budget_micros ?? policy.default_budget_micros, USD),
  });

  const save = async (): Promise<void> => {
    const typedGenerations = parseCount(generations);
    const typedBudget = parseUsdMicros(budget);
    const problem = personLimitProblem(typedGenerations, typedBudget, policy.ceiling);
    if (problem !== null) {
      setMessage(limitProblemText(problem, policy.ceiling, t));
      return;
    }
    await send(() =>
      onSave({
        monthly_generations: typedGenerations.kind === "value" ? typedGenerations.value : null,
        monthly_budget_micros: typedBudget.kind === "value" ? typedBudget.value : null,
      }),
    );
  };
  const clear = async (): Promise<void> => {
    if (await send(onClear)) {
      setGenerations("");
      setBudget("");
    }
  };

  const described = message === null ? undefined : messageId;
  const savedText = (value: string | null): string => value ?? t("backoffice.distribution.person.default");

  return (
    <tr>
      <th scope="row">
        {label}
        {email === null ? null : <span className={styles.secondary}>{email}</span>}
      </th>
      <td>
        {editable ? (
          <>
            <label className={styles.visuallyHidden} htmlFor={generationsId}>
              {t("backoffice.distribution.person.generationsLabel", { name: label })}
            </label>
            <Input
              id={generationsId}
              value={generations}
              onChange={setGenerations}
              inputMode="numeric"
              invalid={message?.field === "generations"}
              describedBy={described}
            />
          </>
        ) : (
          savedText(saved?.monthly_generations?.toString() ?? null)
        )}
      </td>
      <td>
        {editable ? (
          <>
            <label className={styles.visuallyHidden} htmlFor={budgetId}>
              {t("backoffice.distribution.person.budgetLabel", { name: label })}
            </label>
            <Input
              id={budgetId}
              value={budget}
              onChange={setBudget}
              invalid={message?.field === "budget"}
              describedBy={described}
            />
            <AmountPreview text={budget} />
          </>
        ) : (
          savedText(saved?.monthly_budget_micros == null ? null : moneyText(saved.monthly_budget_micros, USD))
        )}
      </td>
      <td aria-live="polite">
        {applies}
        {saved?.above_ceiling === true ? (
          <span className={styles.clamped}>{t("backoffice.distribution.aboveCeiling")}</span>
        ) : null}
      </td>
      {canManage ? (
        <td>
          <div className={styles.actions}>
            {editable ? (
              <Button variant="primary" size="sm" loading={pending} onClick={() => void save()}>
                {t("backoffice.distribution.action.save")}
              </Button>
            ) : null}
            {saved === undefined ? null : (
              <Button variant="secondary" size="sm" disabled={pending} onClick={() => void clear()}>
                {t("backoffice.distribution.action.useDefault")}
              </Button>
            )}
          </div>
          <RowAlert id={messageId} message={message} />
        </td>
      ) : null}
    </tr>
  );
}

export interface TeamAllocationRowProps {
  /** The team's name, or `null` for an allocation of a team the organization no longer lists. */
  readonly name: string | null;
  readonly teamId: string;
  readonly saved: number | null;
  readonly policy: OrganizationPolicyView;
  readonly canManage: boolean;
  readonly onSave: (monthlyBudgetMicros: number) => Promise<WriteOutcome>;
  readonly onClear: () => Promise<WriteOutcome>;
}

export function TeamAllocationRow({ name, teamId, saved, policy, canManage, onSave, onClear }: TeamAllocationRowProps): ReactElement {
  const { t } = useTranslation();
  const amountId = useId();
  const messageId = useId();
  const [amount, setAmount] = useState(usdInputOf(saved));
  const { pending, message, setMessage, send } = useRowWrite();
  const label = name ?? t("backoffice.distribution.team.unknown");
  const editable = canManage && name !== null;

  const save = async (): Promise<void> => {
    const typed = parseUsdMicros(amount);
    const problem = teamAllocationProblem(typed, teamId, policy);
    if (problem === "invalid") {
      setMessage({ text: t("backoffice.distribution.problem.invalidAllocation"), field: "budget" });
    } else if (problem === "ambiguous") {
      setMessage({ text: t("backoffice.distribution.problem.thousands"), field: "budget" });
    } else if (problem === "outOfBounds") {
      setMessage({
        text: t("backoffice.distribution.problem.budgetOutOfBounds", { limit: moneyText(MAX_MONTHLY_BUDGET_MICROS, USD) }),
        field: "budget",
      });
    } else if (problem === "required") {
      setMessage({ text: t("backoffice.distribution.problem.requiredAllocation"), field: "budget" });
    } else if (problem === "overBudget" && typed.kind === "value") {
      setMessage({
        text: t("backoffice.distribution.problem.overBudget", {
          total: moneyText(allocatedMicros(policy, teamId) + typed.value, USD),
          budget: moneyText(policy.organization_budget_micros, USD),
        }),
        field: "budget",
      });
    } else if (typed.kind === "value") {
      await send(() => onSave(typed.value));
    }
  };
  const clear = async (): Promise<void> => {
    if (await send(onClear)) setAmount("");
  };

  return (
    <tr>
      <th scope="row">{label}</th>
      <td>
        {editable ? (
          <>
            <label className={styles.visuallyHidden} htmlFor={amountId}>
              {t("backoffice.distribution.team.allocationLabel", { name: label })}
            </label>
            <Input
              id={amountId}
              value={amount}
              onChange={setAmount}
              invalid={message?.field === "budget"}
              describedBy={message === null ? undefined : messageId}
            />
            <AmountPreview text={amount} />
          </>
        ) : saved === null ? (
          t("backoffice.distribution.teams.none")
        ) : (
          moneyText(saved, USD)
        )}
      </td>
      {canManage ? (
        <td>
          <div className={styles.actions}>
            {editable ? (
              <Button variant="primary" size="sm" loading={pending} onClick={() => void save()}>
                {t("backoffice.distribution.action.save")}
              </Button>
            ) : null}
            {saved === null ? null : (
              <Button variant="secondary" size="sm" disabled={pending} onClick={() => void clear()}>
                {t("backoffice.distribution.action.removeAllocation")}
              </Button>
            )}
          </div>
          <RowAlert id={messageId} message={message} />
        </td>
      ) : null}
    </tr>
  );
}
